<template>
  <div class="ofb-page">
    <div class="ofb-page-header">
      <h1 class="ofb-page-title">Сверка SBS, факт и бухгалтерия</h1>
    </div>

    <div class="ofb-filters-card">
      <div class="ofb-filters-card__body">
        <div class="ofb-filters-toolbar">
          <div class="ofb-filters-main">
            <CustomSelect
              :options="residents"
              option-label-key="resident_name"
              option-value-key="resident_id"
              label="Жилой комплекс"
              :value="filters.resident_id"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filters.resident_id"
              class="ofb-filter-field"
              @change="onResident"
            />
            <CustomSelect
              :options="constructiveFilterOptions"
              option-value-key="value"
              option-label-key="label"
              label="Конструктив"
              :value="filters.constructive_name_1C"
              fullWidth
              :useInput="false"
              :disable="!filters.resident_id"
              class="ofb-filter-field"
              @change="onConstructive"
            />
            <CustomSelect
              :options="materialFilterOptions"
              option-value-key="value"
              option-label-key="label"
              label="Материал"
              clearable
              :disable="!filters.resident_id || !materialScopeRows.length"
              :value="filters.material_id"
              fullWidth
              :useInput="false"
              class="ofb-filter-field"
              @change="onMaterial"
            />
          </div>

          <div class="ofb-filters-actions">
            <q-btn
              class="ofb-filter-action-btn"
              outline
              color="grey-7"
              no-caps
              dense
              icon="filter_alt_off"
              label="Сбросить"
              :disable="!filters.resident_id || !hasActiveFilters"
              @click="resetFilters"
            />
            <q-btn
              class="ofb-filter-action-btn"
              color="positive"
              unelevated
              no-caps
              dense
              icon="download"
              label="Excel"
              :disable="!filters.resident_id || loading || exporting"
              :loading="exporting"
              @click="exportExcel"
            />
          </div>
        </div>
      </div>
    </div>

    <q-banner v-if="errorText" rounded class="ofb-banner q-mt-md">
      <div class="ofb-banner__text">{{ errorText }}</div>
    </q-banner>

    <q-linear-progress
      v-if="loading"
      indeterminate
      color="primary"
      track-color="grey-3"
      class="ofb-progress q-mt-md"
      rounded
      size="4px"
    />

    <div v-if="treeRoots.length" class="ofb-tree-card q-mt-md">
      <div class="ofb-tree-toolbar">
        <div class="ofb-table-toolbar__row">
          <div class="ofb-table-toolbar__left">
            <q-btn
              flat
              dense
              no-caps
              size="sm"
              :icon-right="isTreeFullyExpanded ? 'unfold_less' : 'unfold_more'"
              :label="isTreeFullyExpanded ? 'Свернуть всё' : 'Развернуть всё'"
              :disable="loading"
              @click="toggleExpandAll"
            />
          </div>
          <q-input
            v-model="treeSearch"
            class="ofb-tree-search"
            dense
            outlined
            clearable
            placeholder="Поиск"
            @update:model-value="scheduleApplyTreeSearch"
            @clear="applyTreeSearch"
          >
            <template #prepend>
              <q-icon name="search" size="sm" class="text-grey-6" />
            </template>
          </q-input>
        </div>
        <div
          v-if="treeSearchActive && !displayRows.length"
          class="ofb-search-empty"
        >
          По запросу «{{ treeSearchQuery }}» ничего не найдено
        </div>
      </div>

      <div class="ofb-table-scroll">
        <table class="ofb-table" cellspacing="0">
          <colgroup>
            <col class="ofb-col-tree" />
            <col v-for="n in 4" :key="'n' + n" class="ofb-col-num" />
          </colgroup>
          <thead class="ofb-thead">
            <tr class="ofb-tr ofb-tr--head ofb-tr--group">
              <th rowspan="2" class="ofb-th ofb-th--sticky ofb-th--tree ofb-th--stacked">
                <div class="ofb-th__stack ofb-th__stack--levels">
                  <span class="ofb-th__stack-line">Конструктив</span>
                  <span class="ofb-th__stack-line">Ресурс и Ед. изм.</span>
                  <span class="ofb-th__stack-line">Номенклатура 1С БК</span>
                </div>
              </th>
              <th colspan="3" class="ofb-th ofb-th--band ofb-g--sbs ofb-th--groupcell">SBS</th>
              <th colspan="1" class="ofb-th ofb-th--band ofb-g--buh ofb-th--groupcell">Бухгалтерия</th>
            </tr>
            <tr class="ofb-tr ofb-tr--head ofb-tr--sub">
              <th class="ofb-th ofb-g--sbs ofb-th--sub ofb-th--qty">Количество по плановой норме</th>
              <th class="ofb-th ofb-g--sbs ofb-th--sub ofb-th--qty">Количество по факту</th>
              <th class="ofb-th ofb-g--sbs ofb-th--sub ofb-th--qty">Количество списания М19</th>
              <th class="ofb-th ofb-g--buh ofb-th--sub ofb-th--qty">Списание на 8110</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in displayRows"
              :key="row._rowKey"
              :class="[
                'ofb-tr',
                `ofb-tr--${row._rowType}`,
                `ofb-tr--depth-${row._depth}`,
                {
                  'ofb-tr--search-hit': treeSearchActive && rowMatchesSearch(row),
                },
              ]"
            >
              <td
                class="ofb-td ofb-td--tree ofb-th--sticky"
                :style="{ paddingLeft: `${12 + row._depth * 20}px` }"
              >
                <div class="ofb-tree-cell">
                  <button
                    v-if="!row._isLeaf"
                    type="button"
                    class="ofb-expand"
                    :aria-expanded="expanded[row._path] === true"
                    @click="toggle(row._path)"
                  >
                    <span
                      class="ofb-chevron"
                      :class="{ 'ofb-chevron--open': expanded[row._path] === true }"
                      aria-hidden="true"
                    />
                  </button>
                  <span v-else class="ofb-expand ofb-expand--spacer" aria-hidden="true" />
                  <div class="ofb-tree-text">
                    <span
                      v-if="row._rowType === 'constructive'"
                      class="ofb-tree-label ofb-tree-label--constructive"
                    >
                      <OfbSearchHighlight :text="row._label" :query="treeSearchQuery" />
                    </span>
                    <span
                      v-else-if="row._rowType === 'material'"
                      class="ofb-tree-label ofb-tree-label--material"
                    >
                      <OfbSearchHighlight :text="row.material_name || '—'" :query="treeSearchQuery" /><template v-if="row.unit_name && row.unit_name !== '—'"><span class="ofb-material-unit">, <OfbSearchHighlight :text="row.unit_name" :query="treeSearchQuery" /></span></template>
                    </span>
                    <span
                      v-else
                      class="ofb-tree-label ofb-tree-label--nomenclature"
                    >
                      <OfbSearchHighlight :text="row.material_name_1c || '—'" :query="treeSearchQuery" />
                    </span>
                  </div>
                </div>
              </td>
              <td class="ofb-td ofb-num ofb-g--sbs">
                <cell-qty
                  v-if="row._rowType !== 'nomenclature'"
                  :v="row.plan_norm_qty"
                />
                <span v-else class="ofb-dash">—</span>
              </td>
              <td class="ofb-td ofb-num ofb-g--sbs">
                <cell-qty
                  v-if="row._rowType !== 'nomenclature'"
                  :v="row.fact_qty"
                />
                <span v-else class="ofb-dash">—</span>
              </td>
              <td class="ofb-td ofb-num ofb-g--sbs">
                <cell-qty
                  v-if="row._rowType !== 'nomenclature'"
                  :v="row.m19_qty"
                />
                <span v-else class="ofb-dash">—</span>
              </td>
              <td class="ofb-td ofb-td--dev ofb-g--buh">
                <template v-if="row._rowType !== 'nomenclature'">
                  <cell-qty
                    v-if="row.spisanie_8110_qty"
                    :v="row.spisanie_8110_qty"
                  />
                  <span v-else class="ofb-dev-label">в разработке</span>
                </template>
                <span v-else class="ofb-dash">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <q-card
      v-else-if="!loading && searched"
      flat
      bordered
      class="ofb-empty q-mt-md"
    >
      <q-card-section class="column items-center q-py-xl q-px-md text-center">
        <div class="ofb-empty__title">Нет данных</div>
        <div class="ofb-empty__text">Смените фильтры или проверьте данные по выбранному ЖК.</div>
      </q-card-section>
    </q-card>

    <div v-else-if="!filters.resident_id && !loading" class="ofb-hint">
      Выберите жилой комплекс, чтобы загрузить отчёт.
    </div>
  </div>
</template>

<script>
import { h } from 'vue';
import Amount from '@/components/common/Amount.vue';
import CustomSelect from '@/components/form/CustomSelect';
import {
  exportOperFactBkCompareReport,
  getOperFactBkCompareReport,
  getResourceStatementResidents,
  getSbsInagroKonstruktivByResident,
} from '@/views/ResidentialSettings/services';
import {
  buildOperFactBkCompareTree,
  buildOperFactBkExpandedForTree,
  buildOperFactBkMaterialFilterOptions,
  collectOperFactBkExpandablePaths,
  operFactBkBranchHasSearchMatch,
  operFactBkExpandPathsForNode,
  operFactBkNodeMatchesSearch,
} from '@/_helpers/operFactBkCompareTree';

const OfbSearchHighlight = {
  name: 'OfbSearchHighlight',
  props: {
    text: { type: [String, Number], default: '' },
    query: { type: String, default: '' },
  },
  render() {
    const text = String(this.text ?? '');
    const q = String(this.query || '').trim();
    if (!q) return h('span', text || '—');
    const lower = text.toLowerCase();
    const qLower = q.toLowerCase();
    const parts = [];
    let start = 0;
    let idx = lower.indexOf(qLower, start);
    while (idx !== -1) {
      if (idx > start) parts.push({ text: text.slice(start, idx), match: false });
      parts.push({ text: text.slice(idx, idx + q.length), match: true });
      start = idx + q.length;
      idx = lower.indexOf(qLower, start);
    }
    if (start < text.length) parts.push({ text: text.slice(start), match: false });
    if (!parts.length) return h('span', text || '—');
    return h(
      'span',
      parts.map((part, i) =>
        part.match
          ? h('mark', { key: i, class: 'ofb-search-mark' }, part.text)
          : part.text,
      ),
    );
  },
};

const CellQty = {
  name: 'CellQty',
  props: { v: [Number, String] },
  render() {
    const n = Number(this.v);
    if (this.v == null || this.v === '' || !Number.isFinite(n) || n === 0) {
      return h('span', { class: 'ofb-dash' }, n === 0 ? '0' : '—');
    }
    return h(Amount, { value: this.v });
  },
};

export default {
  name: 'OperFactBkCompareReport',
  components: { CustomSelect, CellQty, OfbSearchHighlight },
  data() {
    return {
      residents: [],
      constructives: [],
      materialScopeRows: [],
      filters: {
        resident_id: '',
        constructive_name_1C: null,
        material_id: null,
      },
      flatRows: [],
      treeRoots: [],
      expanded: {},
      loading: false,
      exporting: false,
      searched: false,
      errorText: '',
      treeSearch: '',
      _treeSearchTimer: null,
    };
  },
  computed: {
    treeSearchQuery() {
      return (this.treeSearch || '').trim();
    },
    treeSearchActive() {
      return this.treeSearchQuery.length > 0;
    },
    displayRows() {
      const q = this.treeSearchQuery;
      const rows = [];
      const walk = (nodes, parentOpen) => {
        if (!parentOpen) return;
        for (const n of nodes) {
          if (q && !operFactBkBranchHasSearchMatch(n, q)) continue;
          const open = n._isLeaf || this.expanded[n._path] === true;
          rows.push(n);
          if (!n._isLeaf && n.children?.length) walk(n.children, open);
        }
      };
      walk(this.treeRoots, true);
      return rows;
    },
    isTreeFullyExpanded() {
      const paths = collectOperFactBkExpandablePaths(this.treeRoots);
      if (!paths.length) return false;
      return paths.every((p) => this.expanded[p] === true);
    },
    constructiveFilterOptions() {
      const base = [{ label: 'Все конструктивы', value: null }];
      const seen = new Set();
      for (const item of this.constructives || []) {
        const name =
          item.constructive_name_1C ??
          item.constructive_name_1c ??
          item.naimenovanie;
        if (!name || seen.has(name)) continue;
        seen.add(name);
        base.push({ label: String(name), value: String(name) });
      }
      return base;
    },
    materialFilterOptions() {
      return buildOperFactBkMaterialFilterOptions(this.materialScopeRows);
    },
    hasActiveFilters() {
      const { constructive_name_1C, material_id } = this.filters;
      return (
        (constructive_name_1C != null && constructive_name_1C !== '') ||
        (material_id != null && material_id !== '')
      );
    },
  },
  async mounted() {
    this.residents = (await getResourceStatementResidents()) || [];
  },
  methods: {
    buildReportParams({ withMaterialFilter = true } = {}) {
      const params = { resident_id: this.filters.resident_id };
      if (
        this.filters.constructive_name_1C != null &&
        this.filters.constructive_name_1C !== ''
      ) {
        params.constructive_name_1C = this.filters.constructive_name_1C;
      }
      if (
        withMaterialFilter &&
        this.filters.material_id != null &&
        this.filters.material_id !== ''
      ) {
        params.material_id = this.filters.material_id;
      }
      return params;
    },
    /** При выборе материала раскрыть конструктивы и папки материалов в дереве. */
    applyExpandedForMaterialFilter() {
      const hasMaterial =
        this.filters.material_id != null && this.filters.material_id !== '';
      if (!hasMaterial || !this.treeRoots.length) {
        this.expanded = {};
        return;
      }
      this.expanded = buildOperFactBkExpandedForTree(this.treeRoots);
    },
    rowMatchesSearch(row) {
      return operFactBkNodeMatchesSearch(row, this.treeSearchQuery);
    },
    toggle(path) {
      const next = { ...this.expanded };
      if (next[path] === true) delete next[path];
      else next[path] = true;
      this.expanded = next;
    },
    toggleExpandAll() {
      const paths = collectOperFactBkExpandablePaths(this.treeRoots);
      if (!paths.length) return;
      if (this.isTreeFullyExpanded) {
        this.expanded = {};
        return;
      }
      const next = {};
      for (const p of paths) next[p] = true;
      this.expanded = next;
    },
    scheduleApplyTreeSearch() {
      clearTimeout(this._treeSearchTimer);
      this._treeSearchTimer = setTimeout(() => this.applyTreeSearch(), 320);
    },
    applyTreeSearch() {
      const q = this.treeSearchQuery.toLowerCase();
      if (!q) {
        this.applyExpandedForMaterialFilter();
        return;
      }
      const all = [];
      const walkCollect = (nodes) => {
        for (const n of nodes) {
          all.push(n);
          if (n.children?.length) walkCollect(n.children);
        }
      };
      walkCollect(this.treeRoots);
      const next = {};
      for (const n of all) {
        if (!operFactBkNodeMatchesSearch(n, q)) continue;
        Object.assign(next, operFactBkExpandPathsForNode(n));
      }
      this.expanded = next;
    },
    clearReport() {
      this.errorText = '';
      this.materialScopeRows = [];
      this.flatRows = [];
      this.treeRoots = [];
      this.searched = false;
      this.expanded = {};
      this.treeSearch = '';
    },
    resetFilters() {
      this.filters.constructive_name_1C = null;
      this.filters.material_id = null;
      this.expanded = {};
      if (this.filters.resident_id) this.fetchReport();
    },
    async exportExcel() {
      if (!this.filters.resident_id || this.exporting) return;
      this.exporting = true;
      try {
        const params = this.buildReportParams({ withMaterialFilter: true });
        const resident = this.residents.find(
          (r) => String(r.resident_id) === String(this.filters.resident_id),
        );
        const residentName = resident?.resident_name
          ? resident.resident_name.replace(/[\\/:*?"<>|]/g, '_')
          : 'resident';
        const blob = await exportOperFactBkCompareReport(params);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `oper_fact_bk_compare_${residentName}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        this.errorText = e?.response?.data?.message || e?.message || 'Ошибка экспорта';
      } finally {
        this.exporting = false;
      }
    },
    async onResident(v) {
      this.filters.resident_id = v;
      this.filters.constructive_name_1C = null;
      this.filters.material_id = null;
      this.constructives = [];
      this.materialScopeRows = [];
      if (!v) {
        this.clearReport();
        return;
      }
      this.constructives = (await getSbsInagroKonstruktivByResident(v)) || [];
      this.expanded = {};
      await this.fetchReport();
    },
    async onConstructive(v) {
      this.filters.constructive_name_1C = v ?? null;
      this.filters.material_id = null;
      if (this.filters.resident_id) await this.fetchReport();
    },
    async onMaterial(v) {
      this.filters.material_id = v ?? null;
      this.treeSearch = '';
      if (!this.filters.resident_id) return;
      await this.fetchReportData();
    },
    /** Список материалов для фильтра — без material_id (текущая выборка ЖК/конструктив). */
    async fetchMaterialScope() {
      const res = await getOperFactBkCompareReport(
        this.buildReportParams({ withMaterialFilter: false }),
      );
      this.materialScopeRows = res?.data || [];
    },
    /** Данные отчёта — с material_id в API (фильтр в БД). */
    async fetchReportData() {
      if (!this.filters.resident_id) {
        this.clearReport();
        return;
      }
      this.errorText = '';
      this.loading = true;
      this.searched = true;
      try {
        const res = await getOperFactBkCompareReport(
          this.buildReportParams({ withMaterialFilter: true }),
        );
        const data = res?.data || [];
        this.flatRows = data;
        this.treeSearch = '';
        this.buildTree(data);
        this.applyExpandedForMaterialFilter();
      } catch (e) {
        this.errorText = e?.response?.data?.message || e?.message || 'Ошибка загрузки';
        this.flatRows = [];
        this.treeRoots = [];
        this.expanded = {};
      } finally {
        this.loading = false;
      }
    },
    async fetchReport() {
      if (!this.filters.resident_id) {
        this.clearReport();
        return;
      }
      this.errorText = '';
      this.loading = true;
      this.searched = true;
      try {
        await this.fetchMaterialScope();
        const res = await getOperFactBkCompareReport(
          this.buildReportParams({ withMaterialFilter: true }),
        );
        const data = res?.data || [];
        this.flatRows = data;
        this.treeSearch = '';
        this.buildTree(data);
        this.applyExpandedForMaterialFilter();
      } catch (e) {
        this.errorText = e?.response?.data?.message || e?.message || 'Ошибка загрузки';
        this.materialScopeRows = [];
        this.flatRows = [];
        this.treeRoots = [];
        this.expanded = {};
      } finally {
        this.loading = false;
      }
    },
    buildTree(flat) {
      this.treeRoots = buildOperFactBkCompareTree(flat);
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/assets/styles/quasar.variables.scss" as *;

.ofb-page {
  padding: 8px 12px 48px;
  max-width: 100%;
  min-height: calc(100vh - 56px);
  min-height: calc(100dvh - 56px);
  box-sizing: border-box;
}

.ofb-page-header {
  margin-bottom: 8px;
}

.ofb-page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  color: #0f172a;
}

.ofb-filters-card {
  margin-bottom: 0;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.ofb-filters-card__body {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.ofb-filters-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 12px;
}

.ofb-filters-main {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.ofb-filters-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.ofb-filter-field {
  min-width: 0;
  width: 100%;

  :deep(.custom-select__wrapper) {
    width: 100%;
  }

  :deep(.q-field) {
    width: 100%;
  }

  :deep(.q-field__control) {
    min-height: 40px;
  }

  :deep(.q-field__native) {
    min-height: 24px;
  }
}

.ofb-filter-action-btn {
  font-size: 12px;
  min-height: 36px;
  width: 118px;
  min-width: 118px;
  padding: 0 8px;
}

@media (max-width: 1280px) {
  .ofb-filters-toolbar {
    grid-template-columns: 1fr;
  }

  .ofb-filters-main {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .ofb-filters-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 720px) {
  .ofb-filters-main {
    grid-template-columns: 1fr;
  }

  .ofb-filters-actions {
    width: 100%;
  }

  .ofb-filter-action-btn {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
  }
}

.ofb-tree-card {
  border: 1px solid $gray-light;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  padding-bottom: 0;
}

.ofb-tree-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
}

.ofb-table-scroll {
  overflow: auto;
  max-height: calc(100dvh - 120px);
  max-height: calc(100vh - 120px);
  -webkit-overflow-scrolling: touch;
  border-radius: 0 0 12px 12px;
  scrollbar-gutter: stable;
}

.ofb-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.ofb-col-tree {
  width: 380px;
  min-width: 300px;
}

.ofb-col-num {
  width: 130px;
  min-width: 120px;
}

.ofb-table-toolbar__row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
}

.ofb-table-toolbar__left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.ofb-th,
.ofb-td {
  border: 1px solid $gray-light;
  padding: 6px 8px;
  vertical-align: middle;
}

.ofb-th {
  background: #f1f5f9;
  font-weight: 600;
  text-align: center;
  position: sticky;
  top: 0;
  z-index: 2;
}

.ofb-th--sticky {
  left: 0;
  z-index: 3;
}

.ofb-td--tree.ofb-th--sticky {
  z-index: 1;
}

.ofb-tr--constructive .ofb-td--tree.ofb-th--sticky {
  background: #e9f1fb;
  box-shadow: inset 4px 0 0 #5b8fce;
}

.ofb-tr--material .ofb-td--tree.ofb-th--sticky {
  background: #f8fafc;
  box-shadow: inset 4px 0 0 #94a3b8;
}

.ofb-tr--nomenclature .ofb-td--tree.ofb-th--sticky {
  background: #fff;
  box-shadow: inset 4px 0 0 #cbd5e1;
}

.ofb-tr--constructive .ofb-td {
  background: #e9f1fb;
  font-weight: 600;
}

.ofb-tr--material .ofb-td {
  background: #f8fafc;
  font-weight: 500;
}

.ofb-tr--nomenclature .ofb-td {
  background: #fff;
}

.ofb-tr--constructive .ofb-g--sbs {
  background: #eef4fb;
}

.ofb-tr--constructive .ofb-g--buh {
  background: #faf6eb;
}

.ofb-tr--material .ofb-g--sbs,
.ofb-tr--material .ofb-g--buh {
  background: #f8fafc;
}

.ofb-g--sbs {
  background: #eff6ff;
}

.ofb-g--buh {
  background: #fef3c7;
}

.ofb-th--groupcell {
  font-size: 11px;
}

.ofb-th--sub {
  font-size: 10px;
  font-weight: 500;
}

.ofb-th--qty {
  text-align: center;
  line-height: 1.25;
  white-space: normal;
  min-width: 120px;
  max-width: 150px;
  padding: 6px 4px;
}

.ofb-td--num {
  text-align: right;
  white-space: nowrap;
}

.ofb-th__stack {
  display: flex;
  flex-direction: column;
  width: 100%;
  line-height: 1.3;
  font-weight: 600;
}

.ofb-th__stack--levels .ofb-th__stack-line {
  display: block;
  width: 100%;
  padding: 5px 8px;
  text-align: center;
  white-space: nowrap;
  border-bottom: 1px solid $gray-light;
  font-size: 11px;

  &:last-child {
    border-bottom: none;
  }
}

.ofb-th--stacked {
  padding: 0;
  vertical-align: middle;
}

.ofb-th--tree.ofb-th--stacked {
  background: #f1f5f9;
}

.ofb-tree-cell {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}

.ofb-tree-text {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 6px;
  min-width: 0;
}

.ofb-tree-label--constructive {
  font-weight: 600;
  color: #1e3a5f;
}

.ofb-tree-label--material {
  font-size: 12px;
  font-weight: 400;
  color: #475569;
}

.ofb-tree-label--nomenclature {
  font-size: 12px;
  color: #475569;
}

.ofb-material-unit {
  font-weight: 600;
  color: #1e40af;
}

.ofb-tree-search {
  flex: 0 1 260px;
  width: 260px;
  max-width: 100%;
  min-width: 180px;
  margin-left: auto;
}

.ofb-search-empty {
  padding: 8px 0 0;
  font-size: 13px;
  color: #64748b;
}

:deep(.ofb-search-mark) {
  background: #fef08a;
  color: inherit;
  padding: 0 1px;
  border-radius: 2px;
}

.ofb-tr--search-hit .ofb-tree-label {
  font-weight: 600;
}

.ofb-td--dev {
  text-align: center;
  vertical-align: middle;
}

.ofb-dev-label {
  font-size: 11px;
  color: #64748b;
  font-style: italic;
  white-space: nowrap;
}

.ofb-expand {
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}

.ofb-expand--spacer {
  cursor: default;
}

.ofb-chevron {
  display: inline-block;
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 6px solid #64748b;
  transition: transform 0.15s ease;
}

.ofb-chevron--open {
  transform: rotate(90deg);
}

.ofb-dash {
  color: #94a3b8;
}

.ofb-hint {
  padding: 32px 0;
  text-align: center;
  color: #64748b;
  font-size: 14px;
}

.ofb-empty__text {
  color: #64748b;
  font-size: 14px;
}

.ofb-empty__title {
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 8px;
}
</style>
